import BaseModel from './BaseModel';
import Release from './Release';
import Change from './Change';
import Client from './Client';
import Commit from './Commit';
import Board from './Board.js';
import User from './User';
import { Projects } from '../services';
import NotificationRule from './NotificationRule';
import ProjectDocumentation from './ProjectDocumentation';
import ProjectLabel from './ProjectLabel';
import _ from 'lodash';
import moment from 'moment';
import type { Moment } from 'moment';
import md5 from "crypto-js/md5";

/** Class representing an user. */
class Project extends BaseModel {
    _id: string;
    name: string;
    slug: string;
    createdAt?: string;
    updatedAt?: string;
    syncedAt?: string;
    active: boolean;
    avatar: string;
    members: User[];
    subscribers: string[];
    clients: Client[];
    linkedProjects: Project[];
    repository: {
        url: string;
        branch: string;
        username?: string | null;
        /** Never returned by the API — write-only on create/update. */
        password?: string | null;
        /** True when the server stores a repository password/token. */
        hasCredentials?: boolean;
    };
    commitRules: {
        feature: string;
        fix: string;
        refactor: string;
        ui: string;
        i18n: string;
        test: string;
        doc: string;
        lint: string;
        chore: string;
        build: string;
        dependencies: string;
        release: string;
    };
    tagRules: {
        releaseTag: string
    };
    description: string;
    translation: {
        context: string;
        languages: {
            default: string;
            all: string[];
        };
    };
    notificationSettings?: {
        defaultLanguage?: string;
    };
    readme: {
        path?: string;
        content?: string;
    };
    documentations: ProjectDocumentation[]
    labels: ProjectLabel[]
    notifications: NotificationRule[]
    comments: string;
    nbAttachments: number;
    nbCommits: number;
    nbReleases: number;
    lastRelease?: Release;
    boards: Board[];
    releases: Release[];
    checks?: {
        dependencies: {
            date: Moment,
            results: {
                success: number;
                info: number;
                warning: number;
                error: number;
            },
            packages: [{
                package: string;
                installed: string;
                latest: string;
                status: string;
            }]
        }
    };
    commits: Commit[];
    changes: Change[];
    isFavorite?: boolean;
    sentry: {
        projects: [string];
        issues: {
            priority: string;
            sources: string[];
            exclude_pattern: string;
        }
        releases: {
            targets: string[];
        }
    };
    posture: {
        defaultLanding: 'board' | 'list';
        hiddenTabs: string[];
    };

    constructor(properties: AnyObj) {
        super({});

        this._id               = properties._id;
        this.name              = properties.name;
        this.slug              = properties.slug || '';
        this.createdAt         = properties.createdAt;
        this.updatedAt         = properties.updatedAt;
        this.syncedAt          = properties.syncedAt;
        this.active            = properties.active;
        this.avatar            = properties.avatar;
        this.members           = (properties.members || []).map((m: AnyObj) => _.isString(m) ? m : new User(m));
        this.subscribers       = (properties.subscribers || []).map((s: AnyObj) => _.isString(s) ? s : s?._id).filter(Boolean);
        this.clients           = (properties.clients || []).map((c: AnyObj) => _.isString(c) ? c : new Client(c));
        this.linkedProjects    = (properties.linkedProjects || []).map((p: AnyObj) => {
            if (_.isString(p))
                return p;
            // Avoid recursive linkedProjects population
            return new Project({ ...p, linkedProjects: [] });
        });
        this.repository        = properties.repository || {};
        this.repository.branch = this.repository.branch || 'master';
        this.commitRules       = properties.commitRules;
        this.tagRules          = properties.tagRules;
        this.description       = properties.description;
        this.translation       = properties.translation;
        this.notificationSettings = properties.notificationSettings || { defaultLanguage: 'fr' };
        this.comments          = properties.comments;
        this.readme            = properties.readme;
        this.sentry            = properties.sentry;
        this.posture           = {
            defaultLanding: properties.posture?.defaultLanding === 'list' ? 'list' : 'board',
            hiddenTabs: Array.isArray(properties.posture?.hiddenTabs)
                ? properties.posture.hiddenTabs.filter((tab: string) => typeof tab === 'string')
                : [],
        };
        this.documentations    = (properties.documentations || []).map((c: AnyObj) => new ProjectDocumentation(c));
        this.labels            = (properties.labels || []).map((l: AnyObj) => new ProjectLabel(l));
        this.notifications     = (properties.notifications || []).map((c: AnyObj) => new NotificationRule(c));
        this.nbAttachments     = properties.nbAttachments || 0;
        this.nbCommits         = properties.nbCommits || 0;
        this.nbReleases        = properties.nbReleases || 0;
        if (properties.lastRelease)
            this.lastRelease = new Release(properties.lastRelease);
        if (properties.checks) {
            this.checks = properties.checks;
            if (this.checks?.dependencies?.date)
                this.checks.dependencies.date = moment(this.checks.dependencies.date);
        }

        // virtuals
        this.boards      = (properties.boards || []).map((b: AnyObj) => new Board(b));
        this.releases    = (properties.releases || []).map((r: AnyObj) => new Release(r));
        this.commits     = (properties.commits || []).map((c: AnyObj) => new Commit(c));
        this.changes     = (properties.changes || []).map((c: AnyObj) => new Change(c));
    }

    /**
     * Prepare this object for update.
     * This is used to "normalize", if needed, some properties
     * before to send them.
     *
     * return{BaseModel}
     */
    prepareForUpdate() : AnyObj {
        const prepared = _.cloneDeep(this) as AnyObj;

        // remove virtual properties
        delete(prepared.releases);
        delete(prepared.nbReleases);
        delete(prepared.commits);
        delete(prepared.nbCommits);
        delete(prepared.changes);

        prepared.linkedProjects = (prepared.linkedProjects || [])
            .map((p: AnyObj) => _.isString(p) ? p : p?._id)
            .filter(Boolean);

        prepared.labels = (prepared.labels || [])
            .map((label: AnyObj) => ({
                _id: label._id,
                name: (label.name || '').trim(),
                color: label.color,
            }))
            .filter((label: AnyObj) => label.name && label._id && label.color);

        return prepared;
    }

    /**
     * Check if this project has a configured repository
     *
     * @return {Boolean}
     */
    hasConfiguredRepo() : boolean {
        return (
            this.repository.url?.length > 0 &&
            this.repository.branch?.length > 0
        );
    }

    /**
     * Check if a user is a member of this project
     *
     * @param {User} user
     * @return {Boolean}
     */
    hasMember(user: User) : boolean {
        let isMember = false;
        this.members?.forEach((u: any) => {
            if (_.isString(u) && u === user._id)
                isMember = true;
            else if (!_.isString(u) && u._id === user._id)
                isMember = true;
        });

        return isMember;
    }

    hasSubscriber(user?: User) : boolean {
        if (!user?._id)
            return false;
        return (this.subscribers || []).includes(user._id);
    }

    /**
     * Get the list of this project commits, sorted by decreasing date
     *
     * @return {Commit[]}
     */
    sortedCommits() : Commit[] {
        return (this.commits || []).sort((a: Commit, b: Commit) => {
            if (a.date.isBefore(b.date))
                return 1;
            return a.date.isAfter(b.date) ? -1 : 0;
        });
    }

    /**
     * Get the list of this project releases, sorted by decreasing date
     *
     * @return {Release[]}
     */
    sortedReleases() : Release[] {
        return (this.releases || []).sort((a: Release, b: Release) => {
            return b.versionNumber - a.versionNumber;
        });
    }

    /**
     * Get this project initials
     *
     * @return {String}
     */
    getInitials() {
        if (!this.name)
            return '??';

        return this.name.match(/(\b\S)?/g)?.join("")?.match(/(^\S|\S$)?/g)?.join("");
    }

    /**
     * Get this project avatar url
     *
     * @return {String}
     */
    getAvatarUrl(size: number = 80, noCache: boolean = false) {
        if (this.avatar?.length > 0)
            return [
                Projects.baseUrl,
                Projects.entryPoint,
                this._id,
                'avatar'
            ].join('/') + (noCache ? '?t=' + Date.now() : '');

        return `https://secure.gravatar.com/avatar/${md5(this.name?.toLowerCase()?.trim())}?s=${size}&d=retro`;
    }

    /**
     * Route key used in client URLs (prefer slug, fallback to _id).
     */
    static navKey(project?: string | { slug?: string; _id?: string } | null): string {
        if (!project)
            return '';
        if (typeof project === 'string')
            return project;
        return project.slug || project._id || '';
    }

    /**
     * Build a /projects/... path using the project slug when available.
     */
    static path(project?: string | { slug?: string; _id?: string } | null, ...segments: Array<string | undefined | null>): string {
        const key = Project.navKey(project);
        if (!key)
            return '/projects';
        const extra = segments.filter((segment): segment is string => !!segment).join('/');
        return extra ? `/projects/${key}/${extra}` : `/projects/${key}`;
    }

    /**
     * Build a /projects/edit/... path; optional settings submenu as /tab/:tab.
     * The default "general" tab omits the /tab segment.
     */
    static editPath(project?: string | { slug?: string; _id?: string } | null, tab?: string | null): string {
        const key = Project.navKey(project);
        if (!key)
            return '/projects';
        if (tab && tab !== 'general')
            return `/projects/edit/${key}/tab/${tab}`;
        return `/projects/edit/${key}`;
    }

    /**
     * Public changelog path using the project slug when available.
     */
    static publicChangelogPath(project?: string | { slug?: string; _id?: string } | null): string {
        const key = Project.navKey(project);
        return key ? `/public/projects/${key}/changelog` : '/public/projects';
    }

    /**
     * Whether this project matches a /projects/:id route param.
     */
    matchesRouteParam(param?: string): boolean {
        return !!param && (this.slug === param || this._id === param);
    }

    /**
     * Check if this project has a dependencies result
     *
     * @return {Boolean}
     */
    hasDependeciesCheckResult() {
        if (!this.checks?.dependencies?.results)
            return false;

        const { results } = this.checks.dependencies;

        return (
            results.success > 0 ||
            results.info > 0 ||
            results.warning > 0 ||
            results.error > 0
        );
    }

    /**
     * Get this project check errors
     *
     * @return {Number}
     */
    checkErrors() : number {
        return this.checks?.dependencies?.results?.error || 0;
    }

    /**
     * Get this project check warnings
     *
     * @return {Number}
     */
    checkWarnings() : number {
        return this.checks?.dependencies?.results?.warning || 0;
    }
}

export default Project;
